import { useContext, useState, useEffect, Fragment } from "react";
import { Context } from "Context";
import { mapString, addCurrentRedirectUrl, getCdn } from "utils";
import { Icon, Link } from "@wdpr/groot-components";
import Card from "./Card";
import MessageCountBadge from "./MessageCountBadge";
import NotificationsModalContent from "./NotificationsModalContent";
import appConfig from "config.json";
import Separator from "components/shared/Separator";
import useSessionStorage from "hooks/useSessionStorage";

const { brands, targetOneId } = appConfig;
const accountContainer = "accountContainer";
const containerClass = "syndicated-profile__container";
const loadHeavyNotificationsKey = "navigationAppLoadHeavyNotifications";

const Profile = ({
	userStatusLinks = false, //if true, it should render sign In/sign out links
	username = false, //if true, it should render username link
	reservation = false, //if true, it should render reservation link (available only in aulani and only rendered when the user is logged-in)
	card = false, //if true, card component is rendered (link with the logged-in user's avatar)
	oneId = false, //if true, the component passed in the renderComponent prop gets a handleClick event attached to integrate with OneId
	target = null, //if not empty and oneId prop is true, then a handleClick event is attached to integrate with OneId
	showIcon = false, //if true, the link(s) will be rendered with the icon(s) coming from content
	notLoggedIn = false, //if true, the handleClick will only be attached to the component rendered by renderComponent if the user is not logged-in
	renderComponent = null, //function with the component to which adds the handleClick event with the OneId integration
	redirectOnLogin = false, //if true, a redirection is made after logging in
	newsletter = false, //if true, it should render newsletter signup
	detailed = false, //if true, it should render a composed message from content to login and register
	isMobileView = false, // if true, mobile view is triggered
	baseStyles = false, // if true, the modifier --base-styles is added for keeping the links with the normal link design
}) => {
	const {
		config: {
			absoluteLinksFromBrand,
			brand,
			locale,
			profile,
			showSignInSignOut,
		},
		profileData: {
			isLoading,
			user = null,
			isOneIdSource,
			setUser,
			handleLogin,
			handleLogout,
			handleProfile,
			travelAgent,
			handleSubscription,
			showSubscriptionMessage,
		} = {},
		headerData,
		setModal,
		updatedLocation,
	} = useContext(Context);
	const [, setLoadHeavyNotificationsObject] = useSessionStorage(
		loadHeavyNotificationsKey,
		null
	);
	const [url, setUrl] = useState(null);
	const [messages, setMessages] = useState([]);
	const [isUpdatingMessages, setIsUpdatingMessages] = useState(false);
	const brandConfig = brands?.[brand];
	const isUserTypeRequired = brandConfig?.isUserTypeRequiredForContentRequest;
	const {
		signedIn,
		travelAgentSignedIn,
		signOut,
		travelAgentSignOut,
		signIn,
		travelAgentSignIn,
		reservationContent,
		reservationIcon,
		profile: profileContent,
		card: cardChromeContent,
		defaultAvatarTitle,
		createAccount,
		orLabel,
		orTextLabel,
		pleaseTextLabel,
		signInLink,
		createAccountLink,
		newsletterSignUp,
		subscriptionMessage,
		profileExtended,
		signedInMobile,
		notificationsBadgeLink,
	} = headerData.chrome || {};
	const messageBadgeAnalyticsId =
		headerData.interstitial?.interstitialNotificationsModal
			?.messageBadgeAnalyticsLinkId;
	const signedInContent = signedIn || travelAgentSignedIn;
	const signOutContent = signOut || travelAgentSignOut;
	const signInContent = detailed ? signInLink : signIn || travelAgentSignIn;
	const pleaseLabelContent = detailed ? pleaseTextLabel : null;
	const orLabelContent = detailed ? orTextLabel : orLabel;
	const createAccountContent = detailed ? createAccountLink : createAccount;
	const baseStylesmodifier = baseStyles
		? ` ${containerClass}--base-styles`
		: "";
	const cardContent = profileExtended
		? {
				...signedIn,
				title: "",
				header: profileExtended,
		  }
		: cardChromeContent;

	useEffect(() => {
		if (updatedLocation) {
			if (
				user ||
				((travelAgent?.firstName || travelAgent?.travelAgency?.partnerId) &&
					isUserTypeRequired)
			) {
				setUrl(
					addCurrentRedirectUrl(
						signOutContent?.href || "/authentication/logout/",
						false
					)
				);
			} else {
				setUrl(
					addCurrentRedirectUrl(
						signInContent?.href || "/login/",
						true,
						false,
						brand,
						locale,
						absoluteLinksFromBrand
					)
				);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [updatedLocation, user, travelAgent]);

	useEffect(() => {
		if (user?.messages) {
			setMessages(user.messages);
			if (isUpdatingMessages) {
				setModal({ content: modalContent() });
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user?.messages]);

	if (isLoading) return null;

	if (renderComponent && oneId) {
		const handleClickAttached = (e) => {
			if (target !== targetOneId) return null;
			if (
				!showSignInSignOut ||
				!profile ||
				!isOneIdSource ||
				(notLoggedIn && user)
			)
				return null;
			if (user)
				handleProfile(e, redirectOnLogin ? e?.currentTarget?.href : null);
			else handleLogin(e, redirectOnLogin ? e?.currentTarget?.href : null);
		};
		return renderComponent(handleClickAttached);
	}

	if (!showSignInSignOut || !profile) return null;

	const handleShowNotifications = () => {
		setLoadHeavyNotificationsObject(null);
		setModal({ content: modalContent() });
	};

	const handleHideNotifications = () => {
		setModal(false);
	};

	const handleUpdateNotifications = (messageToRemove) => {
		setIsUpdatingMessages(true);
		const updatedMessages = messages.filter(
			(element) => element.links.self.href !== messageToRemove
		);
		const updatedUser = { ...user };
		updatedUser.messages.receivedInvitations = updatedMessages;
		setUser({ ...updatedUser });
		setMessages([...updatedMessages]);
	};

	const modalContent = () => {
		return (
			<NotificationsModalContent
				action={handleHideNotifications}
				updateNotificationsAction={handleUpdateNotifications}
				messages={messages}
			/>
		);
	};

	const renderSignOutLink = (isExtended = false) => {
		if (!signOutContent) return null;

		// Only use url if it already points to log out, if not, rebuild it
		const isLogoutUrl = !!(
			url &&
			signOutContent?.href &&
			url.includes(signOutContent.href)
		);

		return (
			<div
				className={`${accountContainer} ${containerClass}${
					isExtended ? ` ${containerClass}--extended` : ""
				} ${containerClass}--${brand}${baseStylesmodifier}`}
			>
				<Link
					{...signOutContent}
					href={
						isLogoutUrl
							? url
							: addCurrentRedirectUrl(signOutContent.href, false)
					}
					className={`${containerClass}__link signOut${
						isExtended ? ` ${containerClass}__link--extended` : ""
					}`}
					handleClick={(event) => handleLogout(event)}
				>
					{showIcon && (
						<Fragment>
							<Icon
								icon={profileContent?.icon}
								ariaHidden={true}
								getCdn={getCdn}
							/>
							{signOutContent.title}
						</Fragment>
					)}
				</Link>
			</div>
		);
	};

	const renderProfileExtended = (renderSeparator = true) => (
		<>
			{renderSignOutLink(true)}
			{renderSeparator && <Separator />}
		</>
	);

	const getLinkContent = () => {
		const firstName = (user || travelAgent)?.firstName;
		let href = signedInContent.href;
		let title = mapString(signedInContent.title, firstName);
		if (isMobileView) {
			if (profileExtended) title = firstName;
			if (signedInMobile?.length) {
				const emptyLink = signedInMobile.find((link) => !link.title);
				if (emptyLink) href = emptyLink.href;
			}
		}
		return {
			...signedInContent,
			href,
			title,
		};
	};

	return headerData.chrome ? (
		user ||
		((travelAgent?.firstName || travelAgent?.travelAgency?.partnerId) &&
			isUserTypeRequired) ? (
			<Fragment>
				{username && signedInContent && (
					<div
						className={`${accountContainer} ${containerClass}${
							profileExtended && isMobileView
								? ` ${containerClass}--extended`
								: ""
						} ${containerClass}--guest ${containerClass}--${brand}${baseStylesmodifier}`}
					>
						{profileExtended && isMobileView && (
							<p className={`${containerClass}__header--extended`}>
								{profileExtended}
							</p>
						)}
						<Link
							{...getLinkContent()}
							className={`${containerClass}__link--guest guestSensitive`}
							handleClick={(event) => handleProfile(event)}
						/>
						{user?.membership && notificationsBadgeLink ? (
							<MessageCountBadge
								counter={user?.messages?.itemCount}
								link={{ ...notificationsBadgeLink, title: null }}
							/>
						) : (
							messages && (
								<MessageCountBadge
									action={handleShowNotifications}
									counter={
										parseInt(messages?.itemCount) ||
										parseInt(messages[0]?.itemCount) ||
										(brand === "dvc" && messages.length)
									}
									analyticsLinkId={messageBadgeAnalyticsId}
								/>
							)
						)}
						{isMobileView && (
							<>
								{profileExtended && renderProfileExtended(false)}
								{signedInMobile?.length && (
									<>
										<Separator />
										<ul className={`${containerClass}__mobile-links`}>
											{signedInMobile?.map(({ title, href }, index) =>
												title ? (
													<li key={index}>
														<Link href={href} title={title} />
													</li>
												) : null
											)}
										</ul>
									</>
								)}
							</>
						)}
					</div>
				)}
				{isOneIdSource &&
					username &&
					user?.marketing &&
					!user.subscribed &&
					newsletter &&
					newsletterSignUp &&
					!showSubscriptionMessage && (
						<div className={`${accountContainer} ${containerClass}`}>
							<Link
								{...newsletterSignUp}
								handleClick={(event) => handleSubscription(event)}
							/>
						</div>
					)}
				{isOneIdSource &&
					showSubscriptionMessage &&
					subscriptionMessage &&
					newsletter && (
						<div className={`${accountContainer} ${containerClass}`}>
							<p>{subscriptionMessage}</p>
						</div>
					)}
				{reservation && reservationContent && (
					<div className={`${accountContainer} ${containerClass}`}>
						<Link {...reservationContent} className={`${containerClass}__link`}>
							{showIcon && (
								<Fragment>
									<Icon icon={reservationIcon} getCdn={getCdn} />
									{reservationContent?.title}
								</Fragment>
							)}
						</Link>
					</div>
				)}
				{userStatusLinks && renderSignOutLink()}
				{card && cardContent && (
					<>
						<Card
							username={user?.firstName}
							avatar={user?.avatar}
							content={{
								...cardContent,
								defaultAvatarTitle,
							}}
							extended={!!profileExtended}
						/>
						{profileExtended && renderProfileExtended()}
					</>
				)}
			</Fragment>
		) : userStatusLinks && (signInContent || createAccountContent) ? (
			<div
				className={`${accountContainer} ${containerClass}${
					detailed ? ` ${containerClass}--detailed` : ""
				} ${containerClass}--${brand}${baseStylesmodifier}`}
			>
				{pleaseLabelContent && <span>{pleaseLabelContent}</span>}
				{signInContent && (
					<Link
						{...signInContent}
						href={
							url
								? url
								: addCurrentRedirectUrl(
										signInContent.href,
										true,
										false,
										brand,
										locale,
										absoluteLinksFromBrand
								  )
						}
						className={`${containerClass}__link signIn`}
						handleClick={(event) => handleLogin(event)}
					>
						{showIcon && !createAccountContent && (
							<Fragment>
								<Icon
									icon={profileContent?.icon}
									ariaHidden={true}
									getCdn={getCdn}
								/>
								{signInContent?.title}
							</Fragment>
						)}
					</Link>
				)}
				{orLabelContent && <span>{orLabelContent}</span>}
				{createAccountContent && (
					<Link
						{...createAccountContent}
						href={addCurrentRedirectUrl(
							createAccountContent.href,
							true,
							false,
							brand,
							locale,
							absoluteLinksFromBrand
						)}
						className={`${containerClass}__link`}
						handleClick={(event) => handleLogin(event)}
					/>
				)}
			</div>
		) : null
	) : null;
};

export default Profile;
